import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../store/authContext';
import { orgSettingsApi } from '../../services/api';
import './Settings.css';

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      setError('');
      const res = await orgSettingsApi.get();
      setSettings(res.data);
    } catch {
      setError('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleToggleAutoAssign = async () => {
    if (!settings) return;
    try {
      setError('');
      const res = await orgSettingsApi.update({ auto_assign_leads: !settings.auto_assign_leads });
      setSettings(res.data);
    } catch {
      setError('Failed to update setting.');
    }
  };

  if (loading) {
    return (
      <div className="os-page">
        <div className="os-loading">
          <div className="os-loading-spinner" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="os-page">
      {/* Header */}
      <div className="page-header animate-fade-up">
        <div>
          <p className="section-label" style={{ marginBottom: 4 }}>Configuration</p>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">{user?.tenant_name || 'Organisation'} settings</p>
        </div>
      </div>

      {error && <div className="os-error animate-fade-up">{error}</div>}

      {settings && (
        <>
          {/* Lead Assignment Card */}
          <div className="os-card card animate-fade-up" style={{ animationDelay: '40ms' }}>
            <h2 className="os-card-title">Lead Assignment</h2>
            <div className="os-setting-row">
              <div className="os-setting-info">
                <span className="os-setting-label">Automatic lead assignment</span>
                <span className="os-setting-desc">
                  {settings.auto_assign_leads
                    ? 'New leads are automatically distributed across active team members using round-robin'
                    : 'New leads are assigned to you (admin)'}
                </span>
              </div>
              <button
                className={`toggle-switch ${settings.auto_assign_leads ? 'toggle-switch--on' : ''}`}
                onClick={handleToggleAutoAssign}
                type="button"
              >
                <span className="toggle-thumb" />
              </button>
            </div>
          </div>

          {/* Organisation Info Card */}
          <div className="os-card card animate-fade-up" style={{ animationDelay: '80ms' }}>
            <h2 className="os-card-title">Organisation</h2>
            <div className="os-info-grid">
              <div className="os-info-row">
                <span className="os-info-label">Max users</span>
                <span className="os-info-value">{settings.max_users}</span>
              </div>
              <div className="os-info-row">
                <span className="os-info-label">Leads module</span>
                <span className="os-info-value">
                  <span className={`badge badge-${settings.leads_module_enabled ? 'closed' : 'lost'}`}>
                    <span className="badge-dot" />
                    {settings.leads_module_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </span>
              </div>
              <div className="os-info-row">
                <span className="os-info-label">Booking module</span>
                <span className="os-info-value">
                  <span className={`badge badge-${settings.booking_module_enabled ? 'closed' : 'lost'}`}>
                    <span className="badge-dot" />
                    {settings.booking_module_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
